import React, { useState, useEffect, useRef } from "react"
import { Link, graphql } from "gatsby"
import axios from "axios"
import SEO from "../components/seo"
import serverValidations from "../utils/serverValidations"
import LoginApi from "../api/login-api"
import LoginTokenApi from "../api/login-token-api"
import {
  GetQueryStringValue,
  isStringNullOrEmpty,
  setCookie,
  getCookie,
} from "../utils/utils"
import Img from "gatsby-image"

import { Formik, withFormik, Form, Field, ErrorMessage } from "formik"
import * as yup from "yup"

import styles from "../styles/login.module.scss"
import formStyles from "../styles/form-styles.module.scss"

import chevronLeft from "../images/chevron-left.svg"
import circleSuccess from "../images/circle-success.svg"
import circleError from "../images/circle-x.svg"
import appleStoreBadge from "../images/apple-app-store-badge.svg"

export const query = graphql`
  query LOGIN {
    wpgraphql {
      corePageBy(corePageId: 7135) {
        acfLoginFeature {
          eyebrow
          title
          subtext
          featureImage {
            sourceUrl
            altText
            imageFile {
              childImageSharp {
                fixed(width: 640, webpQuality: 90) {
                  ...GatsbyImageSharpFixed_withWebp_noBase64
                }
              }
            }
          }
        }
        seo {
          metaDesc
          title
          opengraphTitle
          opengraphDescription
          twitterDescription
          twitterTitle
        }
      }
    }
  }
`

let loginSchema = yup.object().shape({
  userfield_Username: yup
    .string()
    .trim()
    .min(8, "Email must have 8 characters")
    .required("Must enter an email address")
    .email("Must be a valid email address"),
  userfield_Password: yup
    .string()
    .trim()
    .required("Must enter a password"),
})

let forgotPasswordSchema = yup.object().shape({
  email: yup
    .string()
    .min(8, "Email must have 8 characters")
    .required("Must enter an email address")
    .email("Must be a valid email address"),
})

function useEffectSkipFirst(fn, arr) {
  const isFirst = useRef(true)

  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false
      return
    }
    fn()
  }, arr)
}

let redir = '';
if (typeof window !== "undefined") {
  redir = GetQueryStringValue("redir");
}

// LOGIN FORM //
const LogInForm = ({ handleSetForm, isRecoveryAlert }) => {
  const emailRef = useRef(null)
  const formRef = useRef(null)

  const [isPasswordRecovered, setPasswordRecovered] = useState(false)

  useEffect(() => {
    emailRef.current.focus()
    const userToken = getCookie("ccuserToken")

    const userAuthTokenInfo = {
      UserAuthToken: userToken,
      WebsiteID: 8,
    }

    if (
      !isStringNullOrEmpty(userAuthTokenInfo.UserAuthToken) ||
      !isStringNullOrEmpty(userAuthTokenInfo.WebsiteID) ||
      !(userAuthTokenInfo.WebsiteID <= 0)
    ) {
      LoginTokenApi(userAuthTokenInfo, loadLoginPage)
    } else {
      loadLoginPage()
    }
  }, [])

  const loadLoginPage = () => {
    const source = GetQueryStringValue("source")
    if (source.length > 0 && source === "passwordrecovery") {
      setPasswordRecovered(true)
    }
  }

  const loginWithOkta = async (values, setValues) => {
    const redir = GetQueryStringValue("redir")

    setValues({
      ...values,
      userfield_redir: redir.length > 0 ? redir : "admin-dashboard.aspx",
      userfield_IdP: 1,
      userfield_RememberMe: false,
    })

    await Promise.resolve()
    formRef.current.submit()
  }

  const submitLoginForm = (
    values,
    { setValues, setSubmitting, resetForm, setStatus, setFieldError }
  ) => {
    setStatus({ isInvalid: false })

    const loginInfo = {
      Username: values.userfield_Username.trim(),
      Password: values.userfield_Password.trim(),
      isRememberMe: values.isRememberMe,
      WebsiteID: 8,
    }

    // authenticate
    return LoginApi(values, loginInfo, setCookie, setValues, setStatus, formRef)
  }

  const Form = withFormik({
    // enableReinitialize: false,
    handleSubmit: submitLoginForm,
    validationSchema: loginSchema,
    validateOnChange: false,
    validateOnBlur: false,
    mapPropsToValues: props => ({
      // hidden fields
      userfield_redir: redir.length > 0 ? redir : "admin-dashboard.aspx",
      userfield_firstTimeLogin: "",
      userfield_RememberMe: "",
      userfield_websitedomain: process.env.GATSBY_WEBSITE_DOMAIN_NAME,
      userfield_token: false,
      userfield_IdP: "",

      userfield_Username: "", // email
      userfield_Password: "",
      isRememberMe: false,
    }),
    mapPropsToStatus: props => ({
      isInvalid: false,
    }),
  })(props => {
    const {
      handleBlur,
      handleChange,
      handleSubmit,
      isSubmitting,
      status,
      setValues,
      touched,
      errors,
      values,
    } = props

    return (
      <form
        method="POST"
        onSubmit={handleSubmit}
        ref={formRef}
        action={process.env.GATSBY_LOGIN_URL}
      >
        <input
          id="userfield_redir"
          name="userfield_redir"
          type="hidden"
          value={values.userfield_redir}
        />
        <input
          id="userfield_firstTimeLogin"
          name="userfield_firstTimeLogin"
          type="hidden"
          value={values.userfield_firstTimeLogin}
        />
        <input
          id="userfield_RememberMe"
          name="userfield_RememberMe"
          type="hidden"
          value={values.userfield_RememberMe}
        />
        <input
          id="userfield_websitedomain"
          name="userfield_websitedomain"
          type="hidden"
          value={values.userfield_websitedomain}
        />
        <input
          id="userfield_token"
          name="userfield_token"
          type="hidden"
          value={values.userfield_token}
        />
        <input
          id="userfield_IdP"
          name="userfield_IdP"
          type="hidden"
          value={values.userfield_IdP}
        />

        <div className={styles.formBox}>
          <div className={styles.titleContainer}>
            <h1>Sign In</h1>
            <div className={styles.noAccount}>
              Don't have an account yet?{" "}
              <Link to="/pricing" className={styles.getStarted}>
                Get Started Now
              </Link>
            </div>
          </div>

          {status.isInvalid && (
            <div className={`${styles.alertBox} ${styles.error}`}>
              <img src={circleError} alt="error" />
              <div>Invalid username or password</div>
            </div>
          )}

          {isPasswordRecovered && !status.isInvalid && (
            <div className={`${styles.alertBox}`}>
              <img src={circleSuccess} alt="success icon" />
              <div>
                You changed your password successfully! Use your new password to
                log into your account
              </div>
            </div>
          )}

          <div className={styles.inputRow}>
            <label className={formStyles.label} htmlFor="userfield_Username">
              <div>Email Address</div>
            </label>
            <input
              className={`${
                touched.userfield_Username && errors.userfield_Username
                  ? formStyles.error
                  : null
              } ${formStyles.input}`}
              id="userfield_Username"
              name="userfield_Username"
              type="text"
              value={values.userfield_Username}
              onBlur={handleBlur}
              onChange={handleChange}
              ref={emailRef}
            />
            <ErrorMessage name="userfield_Username">
              {msg => <div className={formStyles.errorTip}>{msg}</div>}
            </ErrorMessage>
          </div>
          {/* password */}
          <div className={styles.inputRow}>
            <label className={formStyles.label} htmlFor="userfield_Password">
              <div>Password</div>
            </label>
            <input
              className={`${
                touched.userfield_Password && errors.userfield_Password
                  ? formStyles.error
                  : null
              } ${formStyles.input}`}
              name="userfield_Password"
              id="userfield_Password"              
              value={values.userfield_Password}
              onBlur={handleBlur}
              onChange={handleChange}
              type="password"
            />
            <ErrorMessage name="userfield_Password">
              {msg => <div className={formStyles.errorTip}>{msg}</div>}
            </ErrorMessage>
          </div>

          {/* remember me and forgot password */}
          <div className={styles.rememberForgot}>
            <div className={formStyles.checkboxContainer}>
              <input
                name="isRememberMe"
                id="isRememberMe"
                type="checkbox"
                checked={values.isRememberMe}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <label htmlFor="isRememberMe">Remember me</label>
            </div>
            <div onClick={handleSetForm} className={styles.forgotPassword}>
              Forgot password?
            </div>
          </div>

          <button
            data-testid="submitButton"
            type="submit"
            className={styles.signInButton}
            disabled={isSubmitting}
            style={{ opacity: isSubmitting ? ".5" : "1" }}
          >
            Sign in
          </button>
          <div className={styles.orDivider}>
            <div className={styles.line} />
            <div className={styles.or}>OR</div>
            <div className={styles.line} />
          </div>
          <button
            type="button"
            onClick={() => loginWithOkta(values, setValues)}
            className={styles.ssoButton}
          >
            Single Sign-on
          </button>
        </div>
      </form>
    )
  })

  return <Form />
}

const ForgotPasswordForm = ({ handleSetForm, handleSetAlert }) => {
  const [submitMessage, setSubmitMessage] = useState(null)

  const submitResetForm = (values, { setSubmitting, resetForm, setErrors }) => {
    setSubmitMessage(null)

    const forgotpasswordInfo = {
      EmailAddress: values.email,
      WebsiteID: 8,
    }

    return axios
      .post(
        `${process.env.GATSBY_API_ENDPOINT}/PasswordRecovery/ForgotPassword`,
        forgotpasswordInfo
      )
      .then(response => {
        const result = response.data

        if (result !== null && result.Result !== null) {
          // success
          setSubmitMessage("success")
        } else {
          // error
          showError(result, setErrors)
        }
      })
      .catch(error => {
        showError(error, setErrors)
      })
  }

  const showError = (result, setErrors) => {
    if (result !== null) {
      switch (result.ErrorCode) {
        case 99:
          setErrors(serverValidations.createForgotPasswordSchema(result))
          break
        case 100: // exception
          setSubmitMessage("error") // result=2
          break
        case 101: // email doesn't exist
          setSubmitMessage("no-email") // result=1
          break
        default:
          setSubmitMessage("error") // result=2
      }
    } else {
      alert("An error has occurred. Please try again.")
    }
  }

  return (
    <Formik
      initialValues={{
        email: "",
      }}
      validateOnChange={false}
      validateOnBlur={false}
      validationSchema={forgotPasswordSchema}
      onSubmit={submitResetForm}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isSubmitting,
      }) => (
        <Form onSubmit={handleSubmit}>
          <div className={styles.formBox}>
            <div className={styles.titleContainer}>
              <h1>Reset Your Password</h1>
              <div className={styles.noAccount}>
                Please enter your email address and we'll send you an email with
                a link to reset your password.
              </div>
            </div>

            {submitMessage === "success" && (
              <>
                <div className={styles.alertBox}>
                  <img src={circleSuccess} alt="success" />
                  <div>
                    Email sent! We have sent you a reset link which you can
                    click to reset your password.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleSetForm}
                  className={styles.loginReturn}
                >
                  Back to Login
                </button>
              </>
            )}

            {submitMessage === "error" && (
              <div className={`${styles.alertBox} ${styles.error}`}>
                <img src={circleError} alt="error" />
                <div>
                  An unexpected error occurred when we tried to email you the
                  password. Please contact our service representives.
                </div>
              </div>
            )}

            {submitMessage === "no-email" && (
              <div className={`${styles.alertBox} ${styles.error}`}>
                <img src={circleError} alt="error" />
                <div>
                  The email address you provided doesn't exist in our system.
                </div>
              </div>
            )}

            {(submitMessage === null ||
              submitMessage === "error" ||
              submitMessage === "no-email") && (
              <>
                <div className={styles.inputRow}>
                  <label className={formStyles.label} htmlFor="email">
                    <div>Email Address</div>
                  </label>
                  <Field
                    className={`${
                      touched.email && errors.email ? formStyles.error : null
                    } ${formStyles.input}`}
                    name="email"
                    placeholder="Email Address"
                  />
                  <ErrorMessage name="email">
                    {msg => <div className={formStyles.errorTip}>{msg}</div>}
                  </ErrorMessage>
                </div>

                <button
                  type="submit"
                  className={styles.signInButton}
                  disabled={isSubmitting}
                >
                  Reset Password
                </button>
                <div className={styles.backSignIn}>
                  Return to{" "}
                  <span
                    onClick={handleSetForm}
                    className={styles.backSignInLink}
                  >
                    Login
                  </span>
                </div>
              </>
            )}
          </div>
        </Form>
      )}
    </Formik>
  )
}

const LogIn = ({ data }) => {

  const [isForgotPasswordForm, setForm] = useState(false)
  const handleSetForm = () => setForm(!isForgotPasswordForm)
  const acfFeature = data.wpgraphql.corePageBy.acfLoginFeature
  const seo = data.wpgraphql.corePageBy.seo

  return (
    <div className={styles.container}>
      <SEO {...seo} />
      <div className={styles.loginContainer}>
        <div className={styles.formContainer}>
          <div className={styles.loginHeader}>
            <Link to="/">
              {/* prettier-ignore */}
              <svg height="29" width="191" xmlns="http://www.w3.org/2000/svg"><g fill="none"><path d="M12.834 4.93a5.48 5.48 0 00-1.952-2.008c1.195 2.214 1.19 4.655-.806 7.192-.194.247-2.77 3.23-3.218 5.977l-.014.062 2.039 2.087c.449-2.747 3.024-5.73 3.218-5.978 2.038-2.59 2.004-5.08.733-7.331" fill="#0cb4e3" /><path d="M10.117 10.058c2.033-2.548 2.038-5 .821-7.225a5.949 5.949 0 00-5.992-.016c-2.76 1.6-3.71 5.15-2.12 7.93.026.046 1.38 2.374 3.623 4.951.18.196.165.187.377.427l.014-.062c.457-2.76 3.08-5.757 3.277-6.005" fill="#6457B0" /> <path d="M2.86 10.794a5.754 5.754 0 012.116-7.86 5.973 5.973 0 015.98.016c-.024-.046-.047-.093-.073-.14C9.295.057 5.674-.791 2.918.796A5.754 5.754 0 00.8 8.655c.034.058 2.584 4.046 6.051 7.47-.211-.238-.197-.229-.376-.423-2.239-2.555-3.59-4.862-3.617-4.908" fill="#fe5c93" /> <path d="M13.02 27.851a5.725 5.725 0 01-2.107-7.835l9.933-17.147c1.59-2.744 5.11-3.683 7.862-2.1a5.726 5.726 0 012.107 7.836l-9.933 17.147a5.758 5.758 0 01-4.99 2.87 5.745 5.745 0 01-2.872-.77z" fill="#6457B0" /> <g fill="#6457B0"> <path d="M46.235 22.391l5.513-14.584h-2.612l-4.094 11.355L40.97 7.807h-2.674l5.43 14.584zm8.548.309c1.625 0 2.613-.823 3.086-1.584 0 .802.082 1.214.103 1.275h2.221c-.02-.102-.123-.699-.123-1.604v-4.978c0-1.995-1.173-3.744-4.258-3.744-2.469 0-4.032 1.543-4.217 3.27l2.18.494c.103-1.008.803-1.81 2.057-1.81 1.317 0 1.893.679 1.893 1.522 0 .35-.165.638-.74.72l-2.572.391c-1.707.247-3.024 1.234-3.024 3.024 0 1.584 1.296 3.024 3.394 3.024zm.453-1.872c-.946 0-1.46-.617-1.46-1.296 0-.823.596-1.234 1.336-1.358l2.613-.39v.452c0 1.954-1.152 2.592-2.49 2.592zM66.2 22.7c2.55 0 3.785-1.687 3.785-3.209 0-1.481-.987-2.633-2.88-3.044l-1.522-.309c-.617-.123-1.008-.514-1.008-1.05 0-.616.597-1.151 1.44-1.151 1.337 0 1.769.926 1.851 1.502l2.016-.576c-.164-.988-1.07-2.798-3.867-2.798-2.078 0-3.682 1.481-3.682 3.23 0 1.378.925 2.53 2.695 2.92l1.48.33c.803.164 1.173.576 1.173 1.11 0 .618-.514 1.153-1.502 1.153-1.275 0-1.954-.803-2.036-1.708l-2.078.576C62.19 20.808 63.3 22.7 66.2 22.7zm9.812-.185c.864 0 1.317-.165 1.46-.226v-1.975c-.102.02-.493.082-.822.082-.885 0-1.275-.37-1.275-1.255v-4.649h2.077v-2.118h-2.077V9.309h-2.16v1.501c0 .885-.474 1.564-1.543 1.564h-.514v2.118h1.85v5.04c0 1.872 1.153 2.983 3.004 2.983zM89.034 22.639c3.723 0 5.944-2.366 6.603-4.834l-2.263-.74c-.494 1.645-1.893 3.208-4.34 3.208-2.49 0-4.835-1.81-4.835-5.225 0-3.538 2.407-5.286 4.814-5.286 2.448 0 3.806 1.42 4.238 3.188l2.324-.782c-.658-2.55-2.86-4.73-6.562-4.73-3.785 0-7.344 2.859-7.344 7.61 0 4.752 3.436 7.59 7.365 7.59zm13.285 0c3.004 0 5.205-2.242 5.205-5.328 0-3.065-2.201-5.307-5.205-5.307-2.982 0-5.183 2.242-5.183 5.307 0 3.086 2.2 5.328 5.183 5.328zm0-2.14c-1.46 0-2.797-1.11-2.797-3.188 0-2.078 1.337-3.147 2.797-3.147 1.481 0 2.798 1.07 2.798 3.147 0 2.098-1.317 3.189-2.798 3.189zm9.271 1.831v-5.78c0-1.317.72-2.366 2.078-2.366 1.502 0 2.057.988 2.057 2.222v5.924h2.386v-6.335c0-2.201-1.172-3.97-3.62-3.97-1.11 0-2.304.473-2.962 1.625v-1.338h-2.325V22.33zm12.384 0v-7.94h2.324v-2.078h-2.324v-1.316c0-1.234.843-1.522 1.481-1.522.453 0 .72.061.864.103V7.54c-.165-.082-.658-.206-1.255-.206-2.057 0-3.497 1.358-3.497 3.6v1.378h-1.748v2.078h1.748v7.94zm8.468.309c2.406 0 3.99-1.42 4.525-3.168l-2.016-.638c-.329.988-1.07 1.749-2.51 1.749-1.48 0-2.674-1.09-2.735-2.613h7.323c.02-.04.062-.37.062-.76 0-3.169-1.81-5.205-4.896-5.205-2.53 0-4.875 2.098-4.875 5.266 0 3.394 2.406 5.369 5.122 5.369zm2.242-6.418h-4.917c.083-1.07.947-2.222 2.448-2.222 1.646 0 2.428 1.05 2.469 2.222zm7.076 6.11v-4.773c0-1.872.843-2.942 2.695-2.942.247 0 .514.02.782.062v-2.427a3.417 3.417 0 00-.638-.062c-1.317 0-2.428.638-2.9 1.728v-1.605h-2.325V22.33zm9.71.308c2.406 0 3.99-1.42 4.525-3.168l-2.016-.638c-.33.988-1.07 1.749-2.51 1.749-1.48 0-2.674-1.09-2.735-2.613h7.323c.02-.04.061-.37.061-.76 0-3.169-1.81-5.205-4.895-5.205-2.53 0-4.876 2.098-4.876 5.266 0 3.394 2.407 5.369 5.122 5.369zm2.242-6.418h-4.917c.083-1.07.946-2.222 2.448-2.222 1.646 0 2.427 1.05 2.469 2.222zm7.076 6.11V16.55c0-1.317.72-2.366 2.078-2.366 1.501 0 2.057.988 2.057 2.222v5.924h2.386v-6.335c0-2.201-1.173-3.97-3.62-3.97-1.111 0-2.305.473-2.963 1.625v-1.338h-2.324V22.33zm13.512.308c2.633 0 4.134-1.707 4.607-3.27l-2.098-.762c-.226.761-.946 1.851-2.51 1.851-1.501 0-2.797-1.131-2.797-3.147s1.296-3.106 2.757-3.106c1.522 0 2.2.987 2.427 1.872l2.14-.782c-.412-1.604-1.893-3.291-4.588-3.291-2.88 0-5.122 2.222-5.122 5.307 0 3.045 2.242 5.328 5.184 5.328zm10.964 0c2.407 0 3.99-1.42 4.525-3.168l-2.016-.638c-.329.988-1.07 1.749-2.51 1.749-1.48 0-2.673-1.09-2.735-2.613h7.323c.02-.04.062-.37.062-.76 0-3.169-1.81-5.205-4.896-5.205-2.53 0-4.875 2.098-4.875 5.266 0 3.394 2.406 5.369 5.122 5.369zm2.242-6.418h-4.916c.082-1.07.946-2.222 2.448-2.222 1.645 0 2.427 1.05 2.468 2.222z" /></g></g></svg>
            </Link>
            <Link to="/">
              <button>
                <img src={chevronLeft} alt="chevron left" />
              </button>
            </Link>
          </div>
          {isForgotPasswordForm ? (
            <ForgotPasswordForm handleSetForm={handleSetForm} />
          ) : (
            <LogInForm handleSetForm={handleSetForm} />
          )}
        </div>
      </div>
      <div className={styles.updatesContainer}>
        <div className={styles.textContainer}>
          <div className={styles.newBadge}>NEW</div>
          <h2 className={styles.title}>{acfFeature.title}</h2>
          <a
            href="https://apps.apple.com/us/app/vast-conference/id1457035935"
            target="_blank"
          >
            <img
              className={styles.appleBadge}
              src={"images/apple-badge.png"}
              alt="apple store badge"
            />
          </a>
          <a
            href="https://play.google.com/store/apps/details?id=com.vast.conference"
            target="_blank"
          >
            <img
              className={styles.googlePlayBadge}
              src={"images/google-play-badge.png"}
              alt="google play badge"
            />
          </a>
        </div>
        <div className={styles.imageContainer}>
          <Img
            className={styles.creative}
            fixed={acfFeature.featureImage.imageFile.childImageSharp.fixed}
            alt={acfFeature.featureImage.altText}
          />
        </div>
      </div>
    </div>
  )
}

export default LogIn
export { LogInForm, ForgotPasswordForm }
